import {
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Stack,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  TextField,
} from '@mui/material';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Payment } from '@/types/payment';

import {
  useCancelRentSubscription,
  CancelRentSubscriptionRequest,
} from '@/api/endpoints/payment/useCancelRentSubscription';
import {
  useCancelStripeSubscription,
  CancelStripeSubscriptionRequest,
} from '@/api/endpoints/payment/useCancelStripeSubscription';
import { StripeSubscription } from '@/api/endpoints/payment/useGetStripeSubscriptions';
import {
  useUpdateSubscriptionDate,
  UpdateSubscriptionDateRequest,
} from '@/api/endpoints/payment/useUpdateSubscriptionDate';

import ModalWithForm from '@/components/modals/ModalWithForm';

interface RentSubscriptionCardProps {
  activeSubscription: Payment | StripeSubscription;
  onCancelled: () => void;
  isStripeSubscription?: boolean;
  isPropertyManager?: boolean;
  tenantId?: string;
}

interface CancelSubscriptionFormData {
  cancelAtPeriodEnd: boolean;
}

interface UpdatePaymentDateFormData {
  nextPaymentDate: string;
}

const RentSubscriptionCard: FC<RentSubscriptionCardProps> = ({
  activeSubscription,
  onCancelled,
  isStripeSubscription = false,
  isPropertyManager = false,
  tenantId,
}) => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openUpdateDateDialog, setOpenUpdateDateDialog] = useState(false);

  const cancelForm = useForm<CancelSubscriptionFormData>({
    defaultValues: {
      cancelAtPeriodEnd: true,
    },
  });

  // Helper function to calculate the next billing date based on the subscription start date
  // This is a simple implementation assuming monthly billing
  const calculateNextBillingDate = (startDateStr: string): string => {
    const startDate = new Date(startDateStr);
    const today = new Date();

    // Create a new date for the next billing cycle
    const nextBillingDate = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      startDate.getDate()
    );

    // If the calculated date is in the past, move to the next month
    if (nextBillingDate <= today) {
      nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
    }

    return nextBillingDate.toLocaleDateString();
  };

  // Get the next payment date for the form's default value
  const getNextPaymentDate = () => {
    if (isStripeSubscription && 'current_period_end' in activeSubscription) {
      const stripeSubDate = new Date(activeSubscription.current_period_end);
      // Format date to YYYY-MM-DD required by date input
      return stripeSubDate.toISOString().split('T')[0];
    }

    // For non-Stripe subscriptions, use the calculated next billing date
    if (activeSubscription.date) {
      const nextDate = new Date(calculateNextBillingDate(activeSubscription.date));
      return nextDate.toISOString().split('T')[0];
    }

    // Default to a date 1 month from now if no date available
    const defaultDate = new Date();
    defaultDate.setMonth(defaultDate.getMonth() + 1);
    return defaultDate.toISOString().split('T')[0];
  };

  const updateDateForm = useForm<UpdatePaymentDateFormData>({
    defaultValues: {
      nextPaymentDate: getNextPaymentDate(),
    },
  });

  // Use separate mutations to avoid type errors
  const regularCancelMutation = useCancelRentSubscription({
    onSuccess: () => onCancelled(),
  });

  const stripeCancelMutation = useCancelStripeSubscription({
    onSuccess: () => onCancelled(),
  });

  const updateDateMutation = useUpdateSubscriptionDate({
    onSuccess: () => {
      setOpenUpdateDateDialog(false);
      onCancelled(); // Refresh the subscription list
    },
  });

  const isPending =
    regularCancelMutation.isPending ||
    stripeCancelMutation.isPending ||
    updateDateMutation.isPending;

  const handleOpenDialog = () => {
    setOpenConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenConfirmDialog(false);
    // Reset form values when closing the modal
    cancelForm.reset({
      cancelAtPeriodEnd: true,
    });
  };

  const handleOpenUpdateDateDialog = () => {
    setOpenUpdateDateDialog(true);
  };

  const handleCloseUpdateDateDialog = () => {
    setOpenUpdateDateDialog(false);
    // Reset form values when closing the modal
    updateDateForm.reset({
      nextPaymentDate: getNextPaymentDate(),
    });
  };

  const handleCancelSubscription = async (data: CancelSubscriptionFormData) => {
    if (isStripeSubscription && 'stripe_subscription_id' in activeSubscription) {
      // For Stripe subscriptions
      const stripeRequest: CancelStripeSubscriptionRequest = {
        stripeSubscriptionId: (activeSubscription as StripeSubscription).stripe_subscription_id,
        cancelAtPeriodEnd: data.cancelAtPeriodEnd,
      };

      // If this is a property manager and we have a tenant ID, include it in the request
      if (isPropertyManager && tenantId) {
        stripeRequest.tenant_id = tenantId;
      }

      stripeCancelMutation.mutate(stripeRequest);
    } else {
      // For regular subscriptions
      const regularRequest: CancelRentSubscriptionRequest = {
        subscriptionId: activeSubscription.id,
      };

      // If this is a property manager and we have a tenant ID, include it in the request
      if (isPropertyManager && tenantId) {
        regularRequest.tenant_id = tenantId;
      }

      regularCancelMutation.mutate(regularRequest);
    }
    setOpenConfirmDialog(false);
    return true;
  };

  const handleUpdatePaymentDate = async (data: UpdatePaymentDateFormData) => {
    // Only allow updating Stripe subscriptions
    if (isStripeSubscription && 'stripe_subscription_id' in activeSubscription) {
      const request: UpdateSubscriptionDateRequest = {
        subscriptionId: (activeSubscription as StripeSubscription).stripe_subscription_id,
        nextPaymentDate: new Date(data.nextPaymentDate).toISOString(),
        tenant_id: tenantId,
      };
      updateDateMutation.mutate(request);
    }
    return true;
  };
  // Get the subscription period information and next billing date
  const renderPeriodInfo = () => {
    if (isStripeSubscription && 'current_period_end' in activeSubscription) {
      const stripeSub = activeSubscription as StripeSubscription;
      const periodEndDate = new Date(stripeSub.current_period_end);

      return (
        <Typography variant="body2" color="text.secondary">
          <strong>Next Rent Payment Date:</strong> {periodEndDate.toLocaleDateString()}
        </Typography>
      );
    }

    if (activeSubscription.period) {
      // For regular subscriptions, parse the period and determine next billing date
      // Assuming period is just a text description and not a date
      return (
        <>
          <Typography variant="body2" color="text.secondary">
            <strong>Period:</strong> {activeSubscription.period}
          </Typography>
          {/* Add next billing date if available in the data */}
          {activeSubscription.date && (
            <Typography variant="body2" color="text.secondary">
              <strong>Next Rent Payment Date:</strong>{' '}
              {calculateNextBillingDate(activeSubscription.date)}
            </Typography>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" component="div">
              Active Recurring Rent ACH Payment
            </Typography>
            <Chip label="Active" color="success" size="small" sx={{ textTransform: 'uppercase' }} />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              <strong>Amount:</strong> ${activeSubscription.amount.toFixed(2)}/month
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Started:</strong> {new Date(activeSubscription.date).toLocaleDateString()}
            </Typography>
            {renderPeriodInfo()}
            {isStripeSubscription && (
              <Typography variant="body2" color="text.secondary">
                <strong>ACH Payment ID:</strong> {activeSubscription.subscriptionId || 'N/A'}
              </Typography>
            )}
          </Stack>
        </CardContent>
        <CardActions>
          {isStripeSubscription && !isPropertyManager && (
            <Button
              size="small"
              color="primary"
              onClick={handleOpenUpdateDateDialog}
              disabled={isPending}
              sx={{ mr: 1 }}
            >
              Change Next Payment Date
            </Button>
          )}
          <Button size="small" color="error" onClick={handleOpenDialog} disabled={isPending}>
            Cancel Recurring Payment
          </Button>
        </CardActions>
      </Card>

      {openConfirmDialog && (
        <ModalWithForm
          form={cancelForm}
          maxWidth="sm"
          title="Cancel Recurring Payment"
          buttonColor="error"
          buttonLabel="Yes, Cancel Payment"
          onClose={handleCloseDialog}
          onSubmit={handleCancelSubscription}
        >
          <Typography>
            Are you sure you want to cancel your recurring rent ACH payment? This will stop future
            automatic rent payments. You may need to contact your property manager to set up a
            different payment method.
          </Typography>

          {isStripeSubscription && (
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">Cancellation Options</FormLabel>
              <RadioGroup
                value={cancelForm.watch('cancelAtPeriodEnd').toString()}
                onChange={e => cancelForm.setValue('cancelAtPeriodEnd', e.target.value === 'true')}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Cancel at period end
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Your recurring payment will remain active until the end of the current
                        billing period
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium" color="error.main">
                        Cancel immediately
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Your recurring payment will be canceled right away
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}
        </ModalWithForm>
      )}

      {openUpdateDateDialog && isStripeSubscription && (
        <ModalWithForm
          form={updateDateForm}
          maxWidth="sm"
          title="Change Next Payment Date"
          buttonColor="primary"
          buttonLabel="Update Payment Date"
          onClose={handleCloseUpdateDateDialog}
          onSubmit={handleUpdatePaymentDate}
        >
          <Typography sx={{ mb: 2 }}>
            You can change when your next rent payment will be processed. This will not affect your
            current payment amount, but will change the date of your next automatic payment.
          </Typography>

          <TextField
            label="Next Payment Date"
            type="date"
            fullWidth
            margin="normal"
            {...updateDateForm.register('nextPaymentDate')}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Select the date when you want your next payment to be processed"
          />
        </ModalWithForm>
      )}
    </>
  );
};

export default RentSubscriptionCard;
